import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Box, Typography, Chip, Tooltip, IconButton, Modal, Fade, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { EXP_STATUS_MAP } from '../../utils/constants/financeStatus.constant';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '92vw',
  maxWidth: 1100,
  height: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const COL = {
  idDate: 155,
  type: 105,
  tax: 80,
  amount: 120,
  status: 110,
  applicant: 130,
  actions: 50,
};

const HEADERS = [
  { label: 'ID / Request Date', key: 'idDate', width: COL.idDate },
  { label: 'Order', key: 'order', flex: 1 },
  { label: 'Type', key: 'type', width: COL.type, align: 'center' },
  { label: 'ภาษี', key: 'tax', width: COL.tax, align: 'center' },
  { label: 'ยอดจ่ายจริง', key: 'amount', width: COL.amount, align: 'right' },
  { label: 'Status', key: 'status', width: COL.status, align: 'center' },
  { label: 'Payee', key: 'payee', flex: 1.5 },
  { label: 'Applicant', key: 'applicant', width: COL.applicant },
  { label: '', key: 'actions', width: COL.actions, align: 'center' },
];

export default function DirectExpensesTable({ expenses }) {
  const [detailId, setDetailId] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const fmt = (n) =>
    (typeof n === 'number' ? n : 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });

  if (!expenses || expenses.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">ไม่มีรายการ Expense</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, overflow: 'hidden', overflowX: 'auto' }}>
        {/* Headers */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.7,
            backgroundColor: '#f1f5f9',
            borderBottom: '1px solid #e2e8f0',
            minWidth: 1100,
          }}
        >
          {HEADERS.map((col) => (
            <Typography
              key={col.key}
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              noWrap
              sx={{
                ...(col.flex ? { flex: col.flex, minWidth: 0 } : { width: col.width, flexShrink: 0 }),
                textAlign: col.align || 'left',
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {expenses.map((e, idx) => {
          const code = e.code || e.expenses_code || String(e._id || '').slice(-8);
          const dateRequest = e.date ? dayjs(e.date).format('DD/MM/YYYY') : '-';
          const amount = e.net_price ?? ((e.price || 0) - (e.withholding_tax || 0));
          const applicant = e.employee
            ? formatEmployeeThaiName(e.employee)
            : '-';
          const statusCfg = EXP_STATUS_MAP[e.status] || EXP_STATUS_MAP.PENDING;

          return (
            <Box
              key={e._id || idx}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                px: 2,
                py: 0.9,
                borderBottom: '1px solid #f1f5f9',
                minWidth: 1100,
                '&:hover': { backgroundColor: '#f8fafc' },
              }}
            >
              {/* ID / Request Date */}
              <Box sx={{ width: COL.idDate, flexShrink: 0 }}>
                <Typography variant="body2" fontWeight={700} fontSize="0.78rem" color="#1d4ed8">{code}</Typography>
                <Typography variant="caption" color="text.secondary">{dateRequest}</Typography>
              </Box>

              {/* Order */}
              <Typography variant="body2" fontSize="0.78rem" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {e.name || '-'}
              </Typography>

              {/* Type */}
              <Box sx={{ width: COL.type, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: 0.2 }}>
                {e.expenses_type === 'PAY' ? (
                  <Chip size="small" label="เบิกเพื่อจ่าย" color="primary" sx={{ fontSize: '0.6rem', height: 18 }} />
                ) : e.expenses_type === 'REFUND' ? (
                  <Chip size="small" label="เบิกคืน" color="secondary" sx={{ fontSize: '0.6rem', height: 18 }} />
                ) : (
                  <Chip size="small" label="-" sx={{ fontSize: '0.6rem', height: 18 }} />
                )}
              </Box>

              {/* ภาษี */}
              <Box sx={{ width: COL.tax, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                {e.includes_vat && (
                  <Chip size="small" label="VAT 7%" color="success" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />
                )}
                {e.withholding_tax > 0 && (
                  <Chip size="small" label={`หัก${e.withholding_tax_rate ? ` ${e.withholding_tax_rate}%` : ''}`} color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />
                )}
                {!e.includes_vat && !e.withholding_tax && (
                  <Typography variant="caption" color="text.disabled">-</Typography>
                )}
              </Box>

              {/* ยอดจ่ายจริง */}
              <Box sx={{ width: COL.amount, flexShrink: 0, textAlign: 'right' }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#1e293b', pt: 0.3 }}>
                  ฿{fmt(amount)}
                </Typography>
              </Box>

              {/* Status */}
              <Box sx={{ width: COL.status, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: 0.2 }}>
                <Chip
                  size="small"
                  label={statusCfg.label}
                  sx={{
                    fontSize: '0.6rem', height: 18,
                    backgroundColor: statusCfg.bg, color: statusCfg.color, fontWeight: 600,
                  }}
                />
              </Box>

              {/* Payee */}
              <Box sx={{ flex: 1.5, minWidth: 0 }}>
                <Typography variant="body2" fontSize="0.75rem" noWrap>{e.payee?.name || '-'}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{e.payee?.bank || ''}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{e.payee?.account_number || ''}</Typography>
              </Box>

              {/* Applicant */}
              <Typography variant="body2" fontSize="0.78rem" noWrap sx={{ width: COL.applicant, flexShrink: 0, pt: 0.3 }}>
                {applicant}
              </Typography>

              {/* Actions */}
              <Box sx={{ width: COL.actions, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="ดูรายละเอียด">
                  <IconButton size="small" onClick={() => { setIframeLoaded(false); setDetailId(e._id); }}>
                    <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Detail Modal */}
      <Modal open={!!detailId} onClose={() => setDetailId(null)}>
        <Fade in={!!detailId}>
          <Box sx={modalStyle}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, py: 1, borderBottom: '1px solid #e2e8f0', flexShrink: 0,
            }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">รายละเอียดค่าใช้จ่าย</Typography>
              <IconButton size="small" onClick={() => setDetailId(null)}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            {!iframeLoaded && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={32} />
              </Box>
            )}
            {detailId && (
              <iframe
                src={`/finance/expenses/detail/${detailId}?embed=1`}
                title="expense-detail"
                onLoad={() => setIframeLoaded(true)}
                style={{ display: iframeLoaded ? 'block' : 'none', width: '100%', flex: 1, border: 'none' }}
              />
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
